// app/javascript/controllers/dae20/live_kanban_controller.js
import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

/**
 * Stimulus Controller для live-reload канбана.
 *
 * Подписывается на ActionCable канал Dae20::LiveTableChannel.
 * При получении rows_changed — запрашивает обновлённые карточки,
 * анимирует перемещение и подсветку.
 *
 * Использование:
 * <div data-controller="dae20--live-kanban"
 *      data-dae20--live-kanban-source-code-value="products"
 *      data-dae20--live-kanban-component-id-value="123"
 *      data-dae20--live-kanban-highlight-duration-value="60000">
 * </div>
 */
export default class extends Controller {
  static values = {
    sourceCode: String,
    componentId: String,
    highlightDuration: { type: Number, default: 60000 },
    urlSpace: { type: String, default: '' }
  }

  subscription = null
  consumer = null
  highlightTimers = new Map()
  // PK карточек, которые были перетащены пользователем вручную.
  // Для них live-update пропускает moveCardToCell (предотвращает race condition
  // между Sortable DOM-перемещением и входящим ActionCable сигналом).
  recentlyMovedPks = new Map()

  connect() {
    if (!this.sourceCodeValue) {
      console.warn("[LiveKanban] No sourceCode — skipping subscription")
      return
    }

    console.log("[LiveKanban] Connecting", {
      sourceCode: this.sourceCodeValue,
      componentId: this.componentIdValue
    })

    this.subscribe()
    this.listenForLocalMoves()
  }

  disconnect() {
    this.unsubscribe()
    this.clearAllHighlightTimers()
    this.recentlyMovedPks.forEach(timer => clearTimeout(timer))
    this.recentlyMovedPks.clear()
  }

  // Слушаем custom event от kanban_controller при ручном перетаскивании.
  // Когда пользователь сам перемещает карточку, Sortable уже переставил DOM —
  // последующий live-update должен только обновить HTML (replaceCard), а не
  // пытаться повторно перемещать (moveCardToCell), иначе возникает race condition.
  listenForLocalMoves() {
    this.element.addEventListener('dae20--kanban:cardMoved', (event) => {
      const pk = event.detail?.recordId
      if (pk) this.markAsLocallyMoved(String(pk))
    })
  }

  markAsLocallyMoved(pk) {
    const existingTimer = this.recentlyMovedPks.get(pk)
    if (existingTimer) clearTimeout(existingTimer)

    // Блокируем moveCardToCell для этого PK на 5 секунд
    const timer = setTimeout(() => {
      this.recentlyMovedPks.delete(pk)
    }, 5000)
    this.recentlyMovedPks.set(pk, timer)
  }

  // === Subscription ===

  subscribe() {
    if (this.subscription) return

    try {
      this.consumer = this.consumer || createConsumer()

      this.subscription = this.consumer.subscriptions.create(
        {
          channel: "Dae20::LiveTableChannel",
          source_code: this.sourceCodeValue
        },
        {
          connected: () => {
            console.log("[LiveKanban] Subscribed to", this.sourceCodeValue)
          },
          disconnected: () => {
            console.log("[LiveKanban] Disconnected from", this.sourceCodeValue)
          },
          received: (data) => this.handleReceived(data),
          rejected: () => {
            console.warn("[LiveKanban] Subscription rejected for", this.sourceCodeValue)
          }
        }
      )
    } catch (error) {
      console.error("[LiveKanban] Subscribe error:", error)
    }
  }

  unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  // === Message Handling ===

  handleReceived(data) {
    console.log("[LiveKanban] Received:", data)

    if (data.type === "rows_changed") {
      this.fetchUpdatedCards(data.pks, data.action)
    }
  }

  async fetchUpdatedCards(pks, action) {
    if (!pks || pks.length === 0) return

    // Fade-out existing cards before fetching
    pks.forEach(pk => this.fadeOutCard(pk))

    try {
      const params = new URLSearchParams()
      params.append("component_id", this.componentIdValue)
      pks.forEach(pk => params.append("pks[]", pk))
      if (this.urlSpaceValue) params.append("url_space", this.urlSpaceValue)

      const url = `/dae20/live_kanban_cards?${params.toString()}`

      const response = await fetch(url, {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        credentials: "same-origin"
      })

      if (!response.ok) {
        console.warn("[LiveKanban] Fetch failed:", response.status)
        // Restore faded cards on error
        pks.forEach(pk => this.restoreCard(pk))
        return
      }

      const cardUpdates = await response.json()

      if (!cardUpdates || cardUpdates.length === 0) {
        console.log("[LiveKanban] Empty response — no matching cards")
        pks.forEach(pk => this.restoreCard(pk))
        return
      }

      cardUpdates.forEach(update => {
        this.applyCardUpdate(update, action)
      })

    } catch (error) {
      console.error("[LiveKanban] fetchUpdatedCards error:", error)
      pks.forEach(pk => this.restoreCard(pk))
    }
  }

  applyCardUpdate(update, action) {
    const { pk, html, x_value, y_value } = update
    const pkStr = String(pk)
    const sourceCode = this.sourceCodeValue
    const cardId = `live_kanban_card_${sourceCode}_${pk}`
    const existingCard = document.getElementById(cardId)

    if (action === "create" || !existingCard) {
      // New card — insert at the top of the target cell
      this.insertNewCard(html, x_value, y_value, pk)
    } else {
      // Если карточку только что перетащил пользователь вручную —
      // Sortable уже переместил DOM в правильную ячейку.
      // Только обновляем HTML (replaceCard), не перемещаем.
      if (this.recentlyMovedPks.has(pkStr)) {
        console.log("[LiveKanban] Card", pkStr, "was recently moved locally — replacing in place")
        this.replaceCard(existingCard, html, pk)
        return
      }

      // Update — check if position changed
      const currentContainer = existingCard.closest('.kanban-cards-container')
      const currentX = (currentContainer?.dataset.kanbanX || '').trim()
      const currentY = (currentContainer?.dataset.kanbanY || '').trim()

      if (currentX !== String(x_value).trim() || currentY !== String(y_value).trim()) {
        // Position changed — move card to new cell
        this.moveCardToCell(existingCard, html, x_value, y_value, pk)
      } else {
        // Same position — just replace content
        this.replaceCard(existingCard, html, pk)
      }
    }
  }

  insertNewCard(html, xValue, yValue, pk) {
    const container = this.findCellContainer(xValue, yValue)
    if (!container) {
      console.warn("[LiveKanban] Target cell not found for", { xValue, yValue })
      return
    }

    // Remove empty placeholder if present
    const cell = container.closest('[data-kanban-cell]')
    const placeholder = cell?.querySelector('[data-kanban-empty-placeholder]')
    if (placeholder) placeholder.remove()

    // Insert card at the top
    const temp = document.createElement('div')
    temp.innerHTML = html
    const newCard = temp.firstElementChild

    if (newCard) {
      newCard.style.opacity = '0'
      newCard.style.transform = 'scale(0.9)'
      container.prepend(newCard)

      // Animate in
      requestAnimationFrame(() => {
        newCard.style.transition = 'opacity 0.3s ease-out, transform 0.3s ease-out'
        newCard.style.opacity = '1'
        newCard.style.transform = 'scale(1)'

        setTimeout(() => {
          newCard.style.transition = ''
          newCard.style.transform = ''
          this.applyHighlight(pk)
        }, 300)
      })
    }
  }

  moveCardToCell(existingCard, html, xValue, yValue, pk) {
    const container = this.findCellContainer(xValue, yValue)
    if (!container) {
      console.warn("[LiveKanban] Target cell not found for move", { xValue, yValue })
      this.replaceCard(existingCard, html, pk)
      return
    }

    const oldContainer = existingCard.closest('.kanban-cards-container')

    // Fade out in old position
    existingCard.style.transition = 'opacity 0.3s ease-out, transform 0.3s ease-out'
    existingCard.style.opacity = '0'
    existingCard.style.transform = 'scale(0.8)'

    setTimeout(() => {
      // Remove from old container
      existingCard.remove()

      // Add empty placeholder to old container if now empty
      if (oldContainer && oldContainer.children.length === 0) {
        const cell = oldContainer.closest('[data-kanban-cell]')
        if (cell && !cell.querySelector('[data-kanban-empty-placeholder]')) {
          const placeholder = document.createElement('div')
          placeholder.className = 'text-gray-300 text-xs text-center py-4'
          placeholder.dataset.kanbanEmptyPlaceholder = ''
          placeholder.textContent = '—'
          cell.appendChild(placeholder)
        }
      }

      // Remove empty placeholder from target cell
      const targetCell = container.closest('[data-kanban-cell]')
      const targetPlaceholder = targetCell?.querySelector('[data-kanban-empty-placeholder]')
      if (targetPlaceholder) targetPlaceholder.remove()

      // Insert new card at top of target
      const temp = document.createElement('div')
      temp.innerHTML = html
      const newCard = temp.firstElementChild

      if (newCard) {
        newCard.style.opacity = '0'
        newCard.style.transform = 'scale(0.9)'
        container.prepend(newCard)

        requestAnimationFrame(() => {
          newCard.style.transition = 'opacity 0.3s ease-out, transform 0.3s ease-out'
          newCard.style.opacity = '1'
          newCard.style.transform = 'scale(1)'

          setTimeout(() => {
            newCard.style.transition = ''
            newCard.style.transform = ''
            this.applyHighlight(pk)
          }, 300)
        })
      }
    }, 300)
  }

  replaceCard(existingCard, html, pk) {
    const temp = document.createElement('div')
    temp.innerHTML = html
    const newCard = temp.firstElementChild

    if (newCard) {
      existingCard.replaceWith(newCard)
      // Fade in
      newCard.style.opacity = '0'
      requestAnimationFrame(() => {
        newCard.style.transition = 'opacity 0.3s ease-out'
        newCard.style.opacity = '1'
        setTimeout(() => {
          newCard.style.transition = ''
          this.applyHighlight(pk)
        }, 300)
      })
    }
  }

  findCellContainer(xValue, yValue) {
    // Find the cards container matching the target x/y values
    // Values are compared as trimmed strings to avoid type/formatting mismatches
    const targetX = String(xValue).trim()
    const targetY = String(yValue).trim()

    const containers = this.element.querySelectorAll('.kanban-cards-container')
    for (const container of containers) {
      const cX = (container.dataset.kanbanX || '').trim()
      const cY = (container.dataset.kanbanY || '').trim()
      if (cX === targetX && cY === targetY) {
        return container
      }
    }

    console.warn("[LiveKanban] Cell not found", {
      targetX, targetY,
      available: Array.from(containers).map(c => ({
        x: c.dataset.kanbanX,
        y: c.dataset.kanbanY
      }))
    })
    return null
  }

  fadeOutCard(pk) {
    const sourceCode = this.sourceCodeValue
    const cardId = `live_kanban_card_${sourceCode}_${pk}`
    const card = document.getElementById(cardId)
    if (card) {
      card.style.transition = 'opacity 0.3s ease-out'
      card.style.opacity = '0.3'
    }
  }

  restoreCard(pk) {
    const sourceCode = this.sourceCodeValue
    const cardId = `live_kanban_card_${sourceCode}_${pk}`
    const card = document.getElementById(cardId)
    if (card) {
      card.style.transition = 'opacity 0.3s ease-out'
      card.style.opacity = '1'
      setTimeout(() => { card.style.transition = '' }, 300)
    }
  }

  // === Highlight ===

  applyHighlight(pk) {
    const sourceCode = this.sourceCodeValue
    const cardId = `live_kanban_card_${sourceCode}_${pk}`
    const cardEl = document.getElementById(cardId)

    if (!cardEl) {
      console.log("[LiveKanban] Card not found for highlight:", cardId)
      return
    }

    cardEl.classList.remove("live-kanban-highlight")
    void cardEl.offsetWidth // force reflow
    cardEl.classList.add("live-kanban-highlight")

    const existingTimer = this.highlightTimers.get(pk)
    if (existingTimer) clearTimeout(existingTimer)

    const timer = setTimeout(() => {
      cardEl.classList.remove("live-kanban-highlight")
      this.highlightTimers.delete(pk)
    }, this.highlightDurationValue)

    this.highlightTimers.set(pk, timer)
  }

  clearAllHighlightTimers() {
    this.highlightTimers.forEach(timer => clearTimeout(timer))
    this.highlightTimers.clear()
  }

  // === Helpers ===

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.getAttribute("content") : ""
  }
}
